'use client';

import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useState } from 'react';
import { usePopover } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ListItemText from '@mui/material/ListItemText';

import { fToNow } from 'src/utils/format-time';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { CustomPopover } from 'src/components/custom-popover';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { useKontakter } from 'src/actions/kontakter';

import { KontaktKort } from './kontakt-kort';
import { KontaktSökDialog } from './kontakt-sok-dialog';

import type { Kontakt } from 'src/actions/kontakter';

// ----------------------------------------------------------------------

export type ContactsPopoverProps = IconButtonProps;

export function ContactsPopover({ sx, ...other }: ContactsPopoverProps) {
  const { open, anchorEl, onClose, onOpen } = usePopover();

  const [vald, setVald] = useState<Kontakt | null>(null);
  const [visaSök, setVisaSök] = useState(false);

  const { kontakter, antal, kontakterLoading, läggTill, dölj, växlaFäst } = useKontakter();

  const renderRad = (kontakt: Kontakt) => (
    <MenuItem key={kontakt.id} sx={{ p: 1, gap: 1.5 }} onClick={() => setVald(kontakt)}>
      {/* Ingen statusprick: det finns ingen närvaroinformation att grunda den på. */}
      <Avatar alt={kontakt.visningsnamn} src={kontakt.avatarUrl ?? undefined}>
        <Iconify width={20} icon={kontakt.ikon} />
      </Avatar>

      <ListItemText
        primary={kontakt.visningsnamn}
        secondary={
          [kontakt.typEtikett, kontakt.senasteKontakt ? fToNow(kontakt.senasteKontakt) : null]
            .filter(Boolean)
            .join(' · ')
        }
        slotProps={{
          primary: { noWrap: true },
          secondary: { sx: { typography: 'caption', color: 'text.disabled' } },
        }}
      />

      {kontakt.fäst && (
        <Iconify width={16} icon="solar:pin-bold" sx={{ color: 'text.disabled', flexShrink: 0 }} />
      )}
    </MenuItem>
  );

  const renderTomt = () => (
    <Box sx={{ px: 3, py: 6, textAlign: 'center' }}>
      <Iconify
        width={44}
        icon="solar:users-group-rounded-bold-duotone"
        sx={{ color: 'text.disabled', mb: 1 }}
      />
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Inga kontakter än — de dyker upp när ni börjar höra av er till varandra.
      </Typography>
    </Box>
  );

  return (
    <>
      <IconButton
        component={m.button}
        whileTap={varTap(0.96)}
        whileHover={varHover(1.04)}
        transition={transitionTap()}
        aria-label="Kontakter"
        onClick={onOpen}
        sx={[
          (theme) => ({ ...(open && { bgcolor: theme.vars.palette.action.selected }) }),
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Iconify icon="solar:users-group-rounded-bold-duotone" width={24} />
      </IconButton>

      <CustomPopover open={open} anchorEl={anchorEl} onClose={onClose}>
        <Box sx={{ pl: 1.5, pr: 1, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Kontakter <span>({antal})</span>
          </Typography>
          <Tooltip title="Lägg till kontakt">
            <IconButton size="small" aria-label="Lägg till kontakt" onClick={() => setVisaSök(true)}>
              <Iconify icon="mingcute:add-line" />
            </IconButton>
          </Tooltip>
        </Box>

        <Scrollbar sx={{ height: 320, width: 320 }}>
          {kontakter.length === 0 && !kontakterLoading ? (
            renderTomt()
          ) : (
            <MenuList>{kontakter.map(renderRad)}</MenuList>
          )}
        </Scrollbar>
      </CustomPopover>

      <KontaktKort
        kontakt={vald}
        onClose={() => setVald(null)}
        onDölj={dölj}
        onVäxlaFäst={växlaFäst}
      />

      <KontaktSökDialog
        open={visaSök}
        onClose={() => setVisaSök(false)}
        onLäggTill={läggTill}
        befintliga={kontakter}
      />
    </>
  );
}
